/**
 * THE CANVAS VOCABULARY — one table, and everything on screen is derived from it.
 *
 * There are exactly ten node types, and they are the ten `src/api/flow-compile.ts` knows how
 * to compile. This file does not add an eleventh, and it must never: a node the compiler has
 * never heard of draws beautifully, saves with a 422, and teaches an owner at 2 AM that the
 * editor lies. The palette, the inspector, the node cards and the read-through are all
 * rendered FROM the table below, so adding a field is one edit in one place and a node type
 * cannot exist in the palette without a card, an inspector and a sentence.
 *
 * THE TOOL LISTS are the real `TOOL_NAMES` from src/tools/registry.ts, split the way doctrine
 * 2 splits them: reads are calm and ungated, everything that changes the world outside the
 * call compiles to a GATED proposal a person approves. They are mirrored here rather than
 * imported because the registry is server code (a database pool, a gateway module) and the
 * console must not pull that into the browser bundle. If the two ever drift the server
 * refuses the save and names the tool, which is the failure we want — loud, at save time.
 */
import type { FlowNodeType } from '../../../../src/api/flow-compile.ts';
import type { IconName } from '../iconNames.ts';

export type NdKind = FlowNodeType;

/**
 * React Flow requires node data to be an index-signature object. Keeping the known keys
 * typed alongside it means the inspector cannot invent `messageText` when the compiler reads
 * `text`, which is the single most likely way this screen breaks.
 */
export type NdData = {
  /** say / ask: the words the caller hears. */
  text?: string;
  /** ask: what the answer is called, so a later branch can decide on it. */
  key?: string;
  /** branch: what it is deciding on — an ask key. */
  on?: string;
  /** branch: one per outlet. THE VALUE IS THE HANDLE ID, so it may never be blank. */
  cases?: string[];
  /** lookup / do: the tool name. */
  tool?: string;
  /** message: what kind of message this is. */
  category?: string;
  /** arm: the situation key this switches on. */
  situation?: string;
  /** page: who, for the card only. The runtime pages whoever is on call. */
  who?: string;
  [k: string]: unknown;
};

export type NdFieldKind = 'text' | 'longtext' | 'cases' | 'tool' | 'situation' | 'category';

export type NdField = {
  key: string;
  label: string;
  kind: NdFieldKind;
  required: boolean;
  placeholder?: string;
  hint?: string;
};

/**
 * The visual rank of a node. It is NOT a colour name — the stylesheet decides what each one
 * looks like in each theme, and a screen that hard-codes a hex has already lost dark mode.
 */
export type NdTone =
  | 'structural'  /* entry, end — the skeleton of the call        */
  | 'speech'      /* say                                          */
  | 'question'    /* ask                                          */
  | 'branch'      /* branch                                       */
  | 'read'        /* lookup — calm, ungated                       */
  | 'gated'       /* do — orchid, a proposal                      */
  | 'page'        /* page — somebody's phone rings                */
  | 'message'     /* message                                      */
  | 'mode';       /* arm — changes what EVERY later caller hears   */

export type NdSpec = {
  kind: NdKind;
  /** The palette word. A verb where a verb is honest. */
  label: string;
  /** The eyebrow on the card. */
  eyebrow: string;
  /** One line, in an ISP owner's words, for the palette and the inspector. */
  blurb: string;
  icon: IconName;
  tone: NdTone;
  /** entry and end are the skeleton; the palette renders them apart and only ever once. */
  structural: boolean;
  /** Branch is the only node with more than one outlet. */
  multiOut: boolean;
  hasIn: boolean;
  hasOut: boolean;
  fields: NdField[];
  /** A promise the product makes about this node, rendered on the card. Doctrine, visible. */
  rule?: string;
  defaults: () => NdData;
};

export const NODE_SPECS: Record<NdKind, NdSpec> = {
  entry: {
    kind: 'entry',
    label: 'Call arrives',
    eyebrow: 'Call arrives',
    blurb: 'Where every call into this flow begins. There is exactly one.',
    icon: 'phone-live',
    tone: 'structural',
    structural: true,
    multiOut: false,
    hasIn: false,
    hasOut: true,
    fields: [],
    defaults: () => ({}),
  },

  say: {
    kind: 'say',
    label: 'Say',
    eyebrow: 'The assistant says',
    blurb: 'Speaks a sentence to the caller and carries on.',
    icon: 'speaker',
    tone: 'speech',
    structural: false,
    multiOut: false,
    hasIn: true,
    hasOut: true,
    fields: [
      {
        key: 'text',
        label: 'What the caller hears',
        kind: 'longtext',
        required: true,
        placeholder: 'Thanks for calling Blue Ridge Fiber. The office is closed for the night.',
        hint: 'Write it the way you would say it out loud. This is read to the caller word for word.',
      },
    ],
    defaults: () => ({ text: '' }),
  },

  ask: {
    kind: 'ask',
    label: 'Ask',
    eyebrow: 'The assistant asks',
    blurb: 'Asks a question and remembers the answer under a name.',
    icon: 'help',
    tone: 'question',
    structural: false,
    multiOut: false,
    hasIn: true,
    hasOut: true,
    fields: [
      {
        key: 'text',
        label: 'The question',
        kind: 'longtext',
        required: true,
        placeholder: 'Is your internet completely out, or is it slow?',
      },
      {
        key: 'key',
        label: 'Remember the answer as',
        kind: 'text',
        required: true,
        placeholder: 'reason',
        hint: 'A short name. A branch later on decides on this, so keep it plain: reason, account, urgency.',
      },
    ],
    defaults: () => ({ text: '', key: '' }),
  },

  branch: {
    kind: 'branch',
    label: 'Branch',
    eyebrow: 'Depending on',
    blurb: 'Sends the call one way or another, on an answer you named earlier.',
    icon: 'filter',
    tone: 'branch',
    structural: false,
    multiOut: true,
    hasIn: true,
    hasOut: true,
    fields: [
      {
        key: 'on',
        label: 'Decide on',
        kind: 'text',
        required: true,
        placeholder: 'reason',
        hint: 'The name you gave an earlier answer.',
      },
      {
        key: 'cases',
        label: 'Outcomes',
        kind: 'cases',
        required: true,
        hint: 'Each outcome is one outlet on the card. An outcome cannot be blank — the assistant would not know which way to go.',
      },
    ],
    defaults: () => ({ on: '', cases: ['outage', 'billing'] }),
  },

  lookup: {
    kind: 'lookup',
    label: 'Look up',
    eyebrow: 'The assistant looks up',
    blurb: 'Reads something — an account, an outage, a device. Changes nothing.',
    icon: 'search',
    tone: 'read',
    structural: false,
    multiOut: false,
    hasIn: true,
    hasOut: true,
    fields: [
      { key: 'tool', label: 'What to look up', kind: 'tool', required: true },
    ],
    rule: 'Read-only. Nothing outside the call changes.',
    defaults: () => ({ tool: '' }),
  },

  do: {
    kind: 'do',
    label: 'Do',
    eyebrow: 'The assistant proposes',
    blurb: 'Changes something. It is proposed for your approval, never performed on its own.',
    icon: 'sparkle',
    tone: 'gated',
    structural: false,
    multiOut: false,
    hasIn: true,
    hasOut: true,
    fields: [
      { key: 'tool', label: 'What to do', kind: 'tool', required: true },
    ],
    rule: 'Proposed, not performed. It waits for a person to approve it.',
    defaults: () => ({ tool: '' }),
  },

  page: {
    kind: 'page',
    label: 'Page on-call',
    eyebrow: 'Voicemail, then page',
    blurb: 'The caller leaves a voicemail, then whoever is on call is paged.',
    icon: 'bell',
    tone: 'page',
    structural: false,
    multiOut: false,
    hasIn: true,
    hasOut: true,
    fields: [
      {
        key: 'who',
        label: 'Note on the page (optional)',
        kind: 'text',
        required: false,
        placeholder: 'Fibre cut — needs a truck',
        hint: 'For the card and the page itself. The runtime always pages whoever is on call tonight.',
      },
    ],
    rule: 'The caller is never connected to a technician. Voicemail first, always.',
    defaults: () => ({ who: '' }),
  },

  message: {
    kind: 'message',
    label: 'Take a message',
    eyebrow: 'The caller leaves',
    blurb: 'Records what the caller wants and files it for the morning.',
    icon: 'inbox',
    tone: 'message',
    structural: false,
    multiOut: false,
    hasIn: true,
    hasOut: true,
    fields: [
      { key: 'category', label: 'File it as', kind: 'category', required: false },
    ],
    defaults: () => ({ category: 'customer' }),
  },

  arm: {
    kind: 'arm',
    label: 'Switch the line',
    eyebrow: 'Switches the line to',
    blurb: 'Turns a mode on. Changes what EVERY later caller hears, until somebody releases it.',
    icon: 'bolt',
    tone: 'mode',
    structural: false,
    multiOut: false,
    hasIn: true,
    hasOut: true,
    fields: [
      { key: 'situation', label: 'Mode to switch on', kind: 'situation', required: true },
    ],
    rule: 'This outlives the call. Every caller after it hears the new mode until it is released.',
    defaults: () => ({ situation: '' }),
  },

  end: {
    kind: 'end',
    label: 'End the call',
    eyebrow: 'The call ends',
    blurb: 'Hangs up. Without one, the assistant decides how the call finishes.',
    icon: 'close',
    tone: 'structural',
    structural: true,
    multiOut: false,
    hasIn: true,
    hasOut: false,
    fields: [],
    defaults: () => ({}),
  },
};

/** Palette order — the order somebody builds a flow in, not alphabetical. */
export const PALETTE_ORDER: NdKind[] = [
  'say', 'ask', 'branch', 'lookup', 'do', 'page', 'message', 'arm', 'end',
];

// --------------------------------------------------------------------------- tool catalogue

export type ToolOption = { name: string; label: string; blurb: string };

/** Read-only. Ungated, calm, and safe to put anywhere. */
export const LOOKUP_TOOLS: ToolOption[] = [
  { name: 'lookup_account', label: 'Find the account', blurb: 'Matches the caller to a customer by phone number or address.' },
  { name: 'check_account_standing', label: 'Check the account standing', blurb: 'Whether the account is current, in collections, or suspended.' },
  { name: 'get_outage_state', label: 'Check for a known outage', blurb: 'Whether this address is inside an outage we already know about.' },
  { name: 'get_device_status', label: 'Check the equipment', blurb: 'Whether the customer’s ONT or radio is reachable right now.' },
];

/** World-changing. Every one of these compiles to a gated proposal (doctrine 2). */
export const DO_TOOLS: ToolOption[] = [
  { name: 'create_ticket', label: 'Open a ticket', blurb: 'Files a trouble ticket against the account.' },
  { name: 'schedule_restore_text', label: 'Offer a restore text', blurb: 'Queues a text to the caller when service comes back.' },
  { name: 'notify_oncall', label: 'Notify on-call', blurb: 'Raises an escalation to whoever is on call tonight.' },
  { name: 'flag_call', label: 'Flag the call', blurb: 'Marks the call for somebody to listen to in the morning.' },
];

/** What a message gets filed as. Free text is allowed; these are the ones the inbox groups. */
export const MESSAGE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'customer', label: 'A customer message' },
  { value: 'sales', label: 'A sales enquiry' },
  { value: 'billing', label: 'A billing question' },
  { value: 'repair', label: 'A repair request' },
  { value: 'other', label: 'Something else' },
];

/**
 * The shipped situations, mirrored so the arm node has a real list before /api/situations
 * answers — and if that request fails, instead of an empty select on the one node that
 * changes the whole line. The live list replaces this the moment it lands.
 */
export const FALLBACK_SITUATIONS: { key: string; label: string; kind: 'posture' | 'overlay' }[] = [
  { key: 'ai_answering', label: 'Assistant off', kind: 'posture' },
  { key: 'after_hours', label: 'After hours', kind: 'posture' },
  { key: 'office_open', label: 'Office open', kind: 'posture' },
  { key: 'lunch', label: 'Lunch', kind: 'posture' },
  { key: 'closed_today', label: 'Closed today', kind: 'posture' },
  { key: 'all_hands', label: 'All hands', kind: 'posture' },
  { key: 'outage', label: 'Outage', kind: 'overlay' },
  { key: 'storm', label: 'Storm watch', kind: 'overlay' },
  { key: 'maintenance', label: 'Maintenance', kind: 'overlay' },
];

export function toolsFor(kind: NdKind): ToolOption[] {
  return kind === 'do' ? DO_TOOLS : LOOKUP_TOOLS;
}

/**
 * Older shipped templates name their steps with the tool's pre-rename name. They still run
 * (the server maps them), but the read-through printed the raw identifier — "It looks up:
 * get_account_standing." — on the one panel an owner is meant to read aloud.
 */
const TOOL_ALIASES: Record<string, string> = {
  get_account_standing: 'check_account_standing',
  check_outage: 'get_outage_state',
  check_equipment: 'get_device_status',
  open_ticket: 'create_ticket',
};

/** Whether a tool changes something (a "do" step) rather than reading something. */
export function isDoTool(name: string): boolean {
  const real = TOOL_ALIASES[name] ?? name;
  return DO_TOOLS.some((t) => t.name === real);
}

export function toolLabel(name: string): string {
  const real = TOOL_ALIASES[name] ?? name;
  const hit = [...LOOKUP_TOOLS, ...DO_TOOLS].find((t) => t.name === real);
  if (hit) return hit.label;
  // Never show an identifier: "check_line_quality" reads as "Check line quality".
  const words = real.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'A lookup';
}

// --------------------------------------------------------------------------- small helpers

let seq = 0;

/**
 * Node ids are readable on purpose. They end up in a 422 issue, in an audit row and in a
 * support conversation, and `say_3` survives all three better than a uuid does.
 */
export function newNodeId(kind: NdKind, existing: Iterable<string>): string {
  const taken = new Set(existing);
  seq += 1;
  let n = seq;
  let id = `${kind}_${n}`;
  while (taken.has(id)) {
    n += 1;
    id = `${kind}_${n}`;
  }
  seq = Math.max(seq, n);
  return id;
}

export function newEdgeId(source: string, target: string, handle?: string | null): string {
  return `e_${source}__${handle ? `${handle}__` : ''}${target}`;
}

/** The words a card shows when a field is still empty. Never a blank card. */
export const EMPTY_LINE: Record<NdKind, string> = {
  entry: 'Every call into this flow starts here.',
  say: 'Nothing to say yet — write the sentence the caller hears.',
  ask: 'No question yet.',
  branch: 'Not deciding on anything yet.',
  lookup: 'Nothing chosen to look up yet.',
  do: 'Nothing chosen to do yet.',
  page: 'The caller leaves a voicemail, then on-call is paged.',
  message: 'A message, filed for the morning.',
  arm: 'No mode chosen — this would not switch anything.',
  end: 'The assistant says goodbye and hangs up.',
};
